import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Icon } from '../primitives/Icon';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: ModalSize;
  hideClose?: boolean;
  /** Footer slot. Typically a row of action buttons aligned to the right. */
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Optional className applied to the inner panel for size overrides. */
  panelClassName?: string;
}

const widthMap: Record<ModalSize, string> = {
  sm: 'w-[400px]',
  md: 'w-[560px]',
  lg: 'w-[720px]',
  xl: 'w-[900px]',
  full: 'w-[92vw]',
};

/**
 * Atlas-styled modal — slot-based wrapper over the dialog overlay
 * pattern from `dialog.tsx`. Mirrors the JSX prototype's
 * imperative `open` / `onClose` API rather than Radix's controlled
 * state because module callers integrate with their own
 * useState/useReducer flows. ESC closes; backdrop click closes;
 * inner panel click is stopped from propagating.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  hideClose = false,
  actions,
  children,
  className,
  panelClassName,
}: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  const showHeader = Boolean(title) || !hideClose;

  // Portaled to document.body: without this, Modal renders in place inside
  // whatever the caller happens to be nested under. Every OTHER overlay in
  // this app (Sheet, Dialog) is Radix-based and portals to body by design,
  // so it always appends AFTER Modal's DOM position — a later sibling in the
  // same stacking context paints on top regardless of Modal's own z-50. A
  // Modal opened from inside an already-open Sheet/Dialog would render
  // behind it. Escaping to body puts Modal in the same layering pool as
  // everything else it needs to compete with.
  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-black/40 backdrop-blur-md dark:bg-black/60',
        'animate-in fade-in-0 duration-hover',
        className,
      )}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative max-h-[90vh] overflow-hidden rounded-xl border border-border bg-bg-surface shadow-xl',
          'flex flex-col',
          'animate-in zoom-in-[0.97] fade-in-0 duration-hover',
          widthMap[size],
          panelClassName,
        )}
      >
        {showHeader ? (
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div className="flex flex-col gap-1">
              {title ? <div className="text-h3 text-text-primary">{title}</div> : null}
              {subtitle ? (
                <div className="text-body text-text-secondary">{subtitle}</div>
              ) : null}
            </div>
            {!hideClose ? (
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className={cn(
                  'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-text-secondary',
                  'hover:bg-bg-elevated hover:text-text-primary',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
                  'transition-colors',
                )}
              >
                <Icon name="x" size={16} />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {actions ? (
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            {actions}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
